import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Database, Trash2, UsersRound } from 'lucide-react';
import { useEvalDatasets, useDeleteDataset } from '@/hooks/useEvaluator';
import { toast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { HelpTooltip } from '@/components/ui/HelpTooltip';
import type { LangfuseDataset } from '@/types/evaluator';

interface DatasetBrowserProps {
  orgId: string | undefined;
  selectedDataset: string | null;
  onSelectDataset: (name: string) => void;
  onDatasetDeleted?: () => void;
}

export function DatasetBrowser({ orgId, selectedDataset, onSelectDataset, onDatasetDeleted }: DatasetBrowserProps) {
  const { data, isLoading, error } = useEvalDatasets(orgId);
  const deleteMutation = useDeleteDataset();

  const datasets = data?.datasets ?? [];

  const sorted = [...datasets].sort((a, b) => {
    const aDate = a.created_at ?? '';
    const bDate = b.created_at ?? '';
    return bDate.localeCompare(aDate);
  });

  return (
    <div className="flex w-[330px] shrink-0 flex-col border-r bg-card">
      <div className="flex min-h-[64px] items-center gap-3 border-b px-4 py-3">
        <Database className="h-4 w-4 text-muted-foreground" />
        <div className="min-w-0">
          <h3 className="inline-flex items-center text-sm font-medium text-foreground">
            Populations
            <HelpTooltip text="Groups of personas you can reuse across simulations." />
          </h3>
        </div>
        <span className="ml-auto rounded-md bg-muted px-2 py-0.5 text-xs tabular-nums text-muted-foreground">
          {datasets.length}
        </span>
      </div>

      <div className="flex-1 overflow-auto divide-y">
        {isLoading && (
          <div className="space-y-2 p-3">
            {[1, 2, 3, 4].map((item) => (
              <Skeleton key={item} className="h-16 rounded-md" />
            ))}
          </div>
        )}
        {error && <p className="px-4 py-3 text-sm text-destructive">Failed to load populations.</p>}
        {!isLoading && !error && sorted.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Database className="h-8 w-8 text-muted-foreground/50 mb-2" />
            <p className="text-sm font-medium text-foreground">No populations yet</p>
          </div>
        )}
        {sorted.map((ds: LangfuseDataset) => {
          const isSelected = ds.name === selectedDataset;
          const count = ds.item_count ?? 0;
          const countLabel = count > 0 ? `${count} ${count === 1 ? 'persona' : 'personas'}` : null;
          return (
            <div
              key={ds.name}
              className={`group relative w-full cursor-pointer px-4 py-3 text-left transition hover:bg-muted/40 ${
                isSelected ? 'bg-muted/50' : ''
              }`}
              onClick={() => onSelectDataset(ds.name)}
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                  <UsersRound className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="truncate text-sm font-medium text-foreground">{ds.name}</div>
                  <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                    {countLabel && <span>{countLabel}</span>}
                    {countLabel && ds.created_at && <span>·</span>}
                    {ds.created_at && <span>Created {formatDate(ds.created_at)}</span>}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  {orgId && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          onClick={(e) => e.stopPropagation()}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                          title="Delete population"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete population</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete <span className="font-semibold">{ds.name}</span> and
                            all its items. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteMutation.mutate(
                                { name: ds.name, organizationId: orgId },
                                {
                                  onSuccess: () => {
                                    toast({ title: 'Deleted', description: `"${ds.name}" has been deleted.` });
                                    if (ds.name === selectedDataset) {
                                      onDatasetDeleted?.();
                                    }
                                  },
                                  onError: (err) => {
                                    toast({
                                      title: 'Error',
                                      description: err instanceof Error ? err.message : 'Failed to delete.',
                                      variant: 'destructive',
                                    });
                                  },
                                },
                              );
                            }}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatDate(value: string) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
    }).format(new Date(value));
  } catch {
    return value;
  }
}
